import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { speak, canListen, listenOnce, words, overlap, feedbackTone } from './speech';
import iconBad from '../assets/images/icon_bad.png';
import iconSoso from '../assets/images/icon_soso.png';
import iconGood from '../assets/images/icon_good.png';
import mascotWalk from '../assets/images/mascot_walk.png';
import { isNative, isGuest } from '../utils/native';
import { reminderOn, enableReminder, refreshReminder } from './reminder';
import '../assets/scss/challenge.scss';
import '../assets/scss/v1plus.scss';

const shuffle = (a) => [...a].sort(() => Math.random() - 0.5);
const rate = (wordId, rating) => axios.post('/api/learn/review', { wordId, rating }).catch(() => { });
const levelLabel = (l) => (/^\d$/.test(l) ? `Level ${l}` : l === '회화' ? '회화 필수 단어' : l);

// Build the exercise queue from the lesson payload (easy → hard)
function buildSteps(data, mode) {
  if (mode === 'review') return data.map(w => ({ type: 'choice', w, review: true }));
  const steps = [];
  data.newWords.forEach(w => steps.push({ type: 'card', w }));
  shuffle(data.newWords).forEach(w => steps.push({ type: 'choice', w }));
  if (data.expressions.length) steps.push({ type: 'expr', items: data.expressions });
  const pool = data.newWords.map(w => w.word).filter(w => !w.includes(' '));
  data.sentences.slice(0, 3).forEach(s => steps.push({ type: 'build', s, extra: shuffle(pool).slice(0, 2) }));
  shuffle(data.newWords.filter(w => !w.word.includes(' '))).slice(0, 2).forEach(w => steps.push({ type: 'listen', w }));
  if (data.sentences[3] || data.sentences[0]) steps.push({ type: 'speak', s: data.sentences[3] || data.sentences[0] });
  data.reviews.forEach(w => steps.push({ type: 'choice', w, review: true }));
  return steps;
}

// Bottom purple button in the v1 position
const Footer = ({ children, ...props }) => (
  <div className="footer-btngroup"><button type="button" className="v1-wide" {...props}>{children}</button></div>
);

const Lesson = ({ mode = 'lesson' }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [steps, setSteps] = useState([]);
  const [i, setI] = useState(0);
  const [result, setResult] = useState(null); // null | { ok, answer }
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [done, setDone] = useState(null);
  const [confirmExit, setConfirmExit] = useState(false);
  const [error, setError] = useState('');
  const missed = useRef(new Set()); // words already marked "again" in this lesson
  const home = mode === 'review' ? '/Review' : '/Main';

  useEffect(() => {
    if (!localStorage.getItem('token')) { navigate('/'); return; }
    axios.get(mode === 'review' ? '/api/learn/reviews' : '/api/learn/lesson').then(res => {
      setData(res.data);
      setSteps(buildSteps(res.data, mode));
    }).catch(() => setError('레슨을 불러오지 못했어요. 잠시 후 다시 시도해 주세요.'));
  }, [mode, navigate]);

  const step = steps[i];
  const isLast = i === steps.length - 1;

  const finish = async () => {
    if (mode === 'review') { setDone({ review: true }); return; }
    try {
      const res = await axios.post('/api/learn/complete', {
        correct: score.correct, total: score.total, newWords: data.newWords.length, reviews: data.reviews.length,
      });
      setDone(res.data);
    } catch (e) {
      setDone({ error: true });
    }
  };

  const next = () => {
    setResult(null);
    if (i + 1 < steps.length) setI(i + 1);
    else finish();
  };

  // Grade a question: first attempts count toward the score; wrong answers come back once at the end
  const grade = (ok, answer) => {
    feedbackTone(ok);
    if (!step.retry) setScore(s => ({ correct: s.correct + (ok ? 1 : 0), total: s.total + 1 }));
    const w = step.w;
    if (w && step.review && !step.retry) rate(w.id, ok ? 3 : 1);
    if (w && !ok && !step.review && !missed.current.has(w.id)) { missed.current.add(w.id); rate(w.id, 1); }
    if (!ok && !step.retry) setSteps(s => [...s, { ...step, retry: true }]);
    setResult({ ok, answer });
  };

  const wrong = score.total - score.correct;
  const header = (
    <header id="header">
      <button type="button" className="header-back" aria-label="그만하기" onClick={() => (done || !steps.length ? navigate(home) : setConfirmExit(true))} />
    </header>
  );

  if (error || !data) {
    return <Layout containName="view v1-keep">{header}<p className="v1-guide">{error || '레슨을 준비하는 중...'}</p></Layout>;
  }
  if (!steps.length) {
    return (
      <Layout containName="view v1-keep">
        {header}
        <p className="v1-guide" style={{ marginTop: 60 }}>{mode === 'review' ? '지금 복습할 단어가 없어요. 잘하고 있어요!' : '모든 단어를 다 배웠어요! 복습으로 실력을 다져 보세요.'}</p>
        <Footer onClick={() => navigate(home)}>돌아가기</Footer>
      </Layout>
    );
  }
  // Guest sign-up keeps the guest token, so the login after it takes over this progress
  const join = () => { localStorage.removeItem('token'); navigate('/Login', { state: { save: true } }); };
  if (done) return <Complete done={done} score={score} onHome={() => navigate(home)} onReview={() => navigate('/Review/Words/wrong')} onJoin={join} />;

  const nextLabel = isLast ? '결과 보기' : '다음 문제';

  return (
    <Layout containName="view v1-keep">
      {header}
      <div className="count-wrap">
        <div className="count">문항수 <div className="num-area"><span>{i + 1}</span>/<span>{steps.length}</span></div></div>
        <div className="count wrong">오답 <div className="num-area"><span>{wrong}</span></div></div>
      </div>
      <div className="v1-bar"><i style={{ width: `${(i / steps.length) * 100}%` }} /></div>

      {step.type === 'card' && <CardStep key={i} w={step.w} onRate={(r) => { rate(step.w.id, r); next(); }} />}
      {step.type === 'choice' && <ChoiceStep key={i} w={step.w} review={step.review} result={result} onCheck={grade} onNext={next} nextLabel={nextLabel} />}
      {step.type === 'expr' && <ExprStep items={step.items} onNext={next} />}
      {step.type === 'build' && <BuildStep key={i} s={step.s} extra={step.extra} result={result} onCheck={grade} onNext={next} nextLabel={nextLabel} />}
      {step.type === 'listen' && <ListenStep key={i} w={step.w} result={result} onCheck={grade} onNext={next} nextLabel={nextLabel} />}
      {step.type === 'speak' && <SpeakStep key={i} s={step.s} result={result} onCheck={grade} onNext={next} nextLabel={nextLabel} />}

      {result && <p className={`v1-fb ${result.ok ? 'ok' : 'no'}`} role="status">{result.ok ? '정답이에요!' : `아쉬워요.${result.answer ? ` 정답은 ${result.answer}` : ''}`}</p>}

      {confirmExit && (
        <div className="v1-dim" role="dialog" aria-modal="true">
          <div className="v1-modal">
            <strong>잠깐!</strong>
            <p>지금 그만두면 레슨은 완료로 기록되지 않아요.<br />지금까지 평가한 단어는 저장돼요.</p>
            <button type="button" className="btn" onClick={() => setConfirmExit(false)}>아직 더 풀래</button>
            <button type="button" className="v1-textbtn" onClick={() => navigate(home)}>공부 끝내기</button>
          </div>
        </div>
      )}
    </Layout>
  );
};

// C. New word: v1 flip card, then rate with the v1 face icons
const CardStep = ({ w, onRate }) => {
  const [flipped, setFlipped] = useState(false);
  useEffect(() => { speak(w.word); }, [w.word]);
  const sound = (text) => (e) => { e.stopPropagation(); speak(text); };
  return (
    <>
      <div className={`card ${flipped ? 'active' : ''}`} onClick={() => setFlipped(true)} style={{ cursor: 'pointer' }}>
        <div className="front">
          <p className="grammar">{levelLabel(w.level)} · 새 단어</p>
          <strong className="word">{w.word}</strong>
          <button type="button" className="v1-tap" onClick={() => setFlipped(true)}>뜻을 떠올린 뒤 카드를 눌러 확인하세요</button>
          <button type="button" className="btn-sound" onClick={sound(w.word)}>Listen</button>
        </div>
        <div className="back">
          <p className="grammar">{levelLabel(w.level)}</p>
          <strong className="word" style={{ fontSize: 34 }}>{w.word}</strong>
          <span className="kword">{w.meaning}</span>
          {w.example_en && <p className="ex">{w.example_en}<em>{w.example_kr}</em></p>}
          <button type="button" className="btn-sound" onClick={sound(w.example_en || w.word)}>Listen</button>
        </div>
      </div>
      {flipped && (
        <>
          <p className="v1-faces-q">이 단어를 얼마나 알고 있었나요?</p>
          <div className="v1-faces">
            <button type="button" className="f1" onClick={() => onRate(1)}><img src={iconBad} alt="" />몰라요</button>
            <button type="button" className="f2" onClick={() => onRate(2)}><img src={iconSoso} alt="" />애매해요</button>
            <button type="button" className="f3" onClick={() => onRate(3)}><img src={iconGood} alt="" />알아요</button>
          </div>
        </>
      )}
    </>
  );
};

// D. Pick the meaning
const ChoiceStep = ({ w, review, result, onCheck, onNext, nextLabel }) => {
  const [sel, setSel] = useState(null);
  return (
    <>
      <div className="card">
        <div className="front">
          <p className="grammar">{review ? '복습' : '뜻 고르기'}</p>
          <strong className="word">{w.word}</strong>
          <button type="button" className="btn-sound" onClick={() => speak(w.word)}>Listen</button>
        </div>
      </div>
      <div className="v1-opts">
        {w.choices.map((c, k) => {
          const cls = result ? (c === w.meaning ? 'right' : c === sel ? 'wrong' : '') : (c === sel ? 'sel' : '');
          return <button type="button" key={k} className={cls} disabled={!!result} onClick={() => setSel(c)}>{c}</button>;
        })}
      </div>
      {result ? <Footer onClick={onNext}>{nextLabel}</Footer>
        : <Footer disabled={!sel} onClick={() => onCheck(sel === w.meaning, w.meaning)}>정답 확인</Footer>}
    </>
  );
};

// Today's expressions (no grading)
const ExprStep = ({ items, onNext }) => (
  <>
    <div className="card">
      <div className="front">
        <p className="grammar">오늘의 표현</p>
        <span className="kword">소리 내어 따라 해 보세요</span>
      </div>
    </div>
    <div className="v1-expr">
      {items.map(e => (
        <div key={e.en}>
          <button type="button" className="v1-snd" aria-label="듣기" onClick={() => speak(e.en.replace(/~/g, 'something'))} />
          <span><strong>{e.en}</strong><span>{e.kr}</span>{e.tip && <small>{e.tip}</small>}</span>
        </div>
      ))}
    </div>
    <Footer onClick={onNext}>다음</Footer>
  </>
);

// E. Build the sentence from word chips
const BuildStep = ({ s, extra, result, onCheck, onNext, nextLabel }) => {
  const tokens = useMemo(() => s.en.split(' '), [s.en]);
  const tiles = useMemo(() => {
    const own = new Set(words(s.en));
    return shuffle([...tokens.map((t, k) => ({ k, t })), ...extra.filter(x => !own.has(x.toLowerCase())).map((t, k) => ({ k: 100 + k, t }))]);
  }, [s.en, tokens, extra]);
  const [picked, setPicked] = useState([]);
  const pickedKeys = new Set(picked.map(p => p.k));
  const check = () => { speak(s.en); onCheck(words(picked.map(p => p.t).join(' ')).join(' ') === words(s.en).join(' '), s.en); };
  return (
    <>
      <div className="card">
        <div className="front">
          <p className="grammar">문장 만들기</p>
          <span className="kword">{s.kr}</span>
        </div>
      </div>
      <div className="v1-slot">
        {picked.map(p => <button type="button" key={p.k} className="v1-chip" disabled={!!result} onClick={() => setPicked(picked.filter(x => x.k !== p.k))}>{p.t}</button>)}
      </div>
      <div className="v1-chiprow">
        {tiles.map(p => <button type="button" key={p.k} className={`v1-chip ${pickedKeys.has(p.k) ? 'used' : ''}`} disabled={!!result || pickedKeys.has(p.k)} onClick={() => setPicked([...picked, p])}>{p.t}</button>)}
      </div>
      {result ? <Footer onClick={onNext}>{nextLabel}</Footer> : <Footer disabled={!picked.length} onClick={check}>정답 확인</Footer>}
    </>
  );
};

// F. Listen and type the word
const ListenStep = ({ w, result, onCheck, onNext, nextLabel }) => {
  const [v, setV] = useState('');
  useEffect(() => { speak(w.word); }, [w.word]);
  const check = () => onCheck(v.trim().toLowerCase() === w.word.toLowerCase(), `${w.word} (${w.meaning})`);
  return (
    <>
      <div className="card">
        <div className="front">
          <p className="grammar">듣고 쓰기</p>
          <button type="button" className="v1-bigsound" aria-label="다시 듣기" onClick={() => speak(w.word)} />
          <button type="button" className="v1-hint" onClick={() => speak(w.word, 0.55)}>🐢 천천히 듣기</button>
        </div>
      </div>
      <p className="v1-guide">들리는 영어 단어를 입력하세요</p>
      <input className="v1-text-answer" value={v} onChange={e => setV(e.target.value)} readOnly={!!result} aria-label="들은 단어"
        autoComplete="off" autoCapitalize="off" spellCheck="false" placeholder="English..."
        onKeyDown={e => e.key === 'Enter' && !e.repeat && (result ? onNext() : v.trim() && check())} />
      {result ? <Footer onClick={onNext}>{nextLabel}</Footer> : <Footer disabled={!v.trim()} onClick={check}>정답 확인</Footer>}
    </>
  );
};

// G. Say the sentence (browser speech recognition), skippable
const SpeakStep = ({ s, result, onCheck, onNext, nextLabel }) => {
  const [on, setOn] = useState(false);
  const [msg, setMsg] = useState('');
  const start = async () => {
    setOn(true); setMsg('');
    try {
      const alts = await listenOnce();
      const best = Math.max(...alts.map(a => overlap(s.en, a)));
      onCheck(best >= 0.7, best >= 0.7 ? null : `${s.en} (들린 말: ${alts[0]})`);
    } catch (e) {
      setMsg(e.message === 'not-allowed' ? '마이크 권한을 허용해 주세요.' : '잘 듣지 못했어요. 다시 눌러 말해 보세요.');
    } finally { setOn(false); }
  };
  return (
    <>
      <div className="card">
        <div className="front">
          <p className="grammar">따라 말하기</p>
          <p className="ex" style={{ fontSize: 18, fontWeight: 700 }}>{s.en}<em>{s.kr}</em></p>
          <button type="button" className="btn-sound" onClick={() => speak(s.en)}>Listen</button>
        </div>
      </div>
      {canListen ? (
        <>
          <button type="button" className={`v1-mic ${on ? 'on' : ''}`} aria-label="누르고 말하기" disabled={on || !!result} onClick={start}>
            <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0M12 18v3" /></svg>
          </button>
          <p className="v1-guide">{on ? '듣고 있어요...' : msg || '마이크를 누르고 말해 보세요'}</p>
        </>
      ) : <p className="v1-guide">이 기기에서는 음성 인식을 쓸 수 없어요. 소리 내어 따라 말한 뒤 넘어가세요.</p>}
      {result ? <Footer onClick={onNext}>{nextLabel}</Footer>
        : <div className="footer-btngroup"><button type="button" className="v1-wide v1-ghost" onClick={onNext}>지금은 말하기 어려워요</button></div>}
    </>
  );
};

// H. Lesson complete: the v1 finish panel with XP / accuracy / streak
const Complete = ({ done, score, onHome, onReview, onJoin }) => {
  const lesson = !done.review && !done.error;
  const guest = isGuest(); // one next step at a time: sign-up for guests, otherwise the reminder
  const [reminder, setReminder] = useState(reminderOn() ? 'on' : 'off'); // off | on | denied
  useEffect(() => { if (lesson) refreshReminder(done.dueTomorrow); }, [lesson, done.dueTomorrow]);
  const askReminder = async () => {
    const ok = await enableReminder(done.dueTomorrow).catch(() => false);
    setReminder(ok ? 'on' : 'denied');
  };
  return (
  <Layout header containName='finish v1-keep'>
    <div style={{ width: '100%', maxWidth: 340, margin: '0 auto', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0' }}>
      <img src={mascotWalk} alt="" style={{ height: 120, marginBottom: 20 }} />
      <h2 style={{ fontSize: 28, fontWeight: 'bold', color: '#fff', lineHeight: 1.4, marginBottom: 15 }}>
        {done.review ? <>복습<br />완료!</> : done.error ? <>레슨<br />완료!</> : <>Day {done.day}<br />완료!</>}
      </h2>
      <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.85)', lineHeight: 1.6, marginBottom: 30 }}>
        {done.review ? `${score.total}개 중 ${score.correct}개를 기억했어요.` : done.error ? '기록을 저장하지 못했어요. 인터넷 연결을 확인해 주세요.' : '두벅두벅, 오늘도 한 걸음 걸었어요!'}
      </p>
      {!done.review && !done.error && (
        <div className="score-summary" style={{ width: '100%', background: 'rgba(255,255,255,0.12)', padding: 25, borderRadius: 20, border: '1px solid rgba(255,255,255,0.1)', boxSizing: 'border-box' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', color: '#fff', marginBottom: 15 }}><span>경험치</span><span style={{ fontWeight: 'bold' }}>+{done.xp} XP</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', color: '#fff', marginBottom: 15 }}><span style={{ color: '#52d681', fontWeight: 'bold' }}>정확도</span><span style={{ fontWeight: 'bold' }}>{done.accuracy}%</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', color: '#fff' }}><span style={{ color: '#ffd27a', fontWeight: 'bold' }}>🔥 연속 학습</span><span style={{ fontWeight: 'bold' }}>{done.streak}일</span></div>
        </div>
      )}
      {lesson && <p className="v1-next">내일 복습 {done.dueTomorrow}개 · 새 단어 10개가 기다려요</p>}
      {lesson && !guest && isNative && reminder !== 'on' && (
        <div className="v1-after">
          {reminder === 'denied'
            ? <small>알림이 꺼져 있어요. 휴대폰 설정 → 앱 → 두벅 영워드에서 알림을 켜 주세요.</small>
            : <><span>잊기 전에 내일 이 시간에 알려 드릴까요?</span><button type="button" onClick={askReminder}>🔔 내일 알림 받기</button></>}
        </div>
      )}
      {lesson && reminder === 'on' && isNative && <p className="v1-next">🔔 내일 이 시간에 알려 드릴게요</p>}
      {guest && (
        <div className="v1-after">
          <span>지금 가입하면 오늘 기록이 그대로 저장돼요.</span>
          <button type="button" onClick={onJoin}>가입하고 기록 저장하기</button>
        </div>
      )}
      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 12, marginTop: 30 }}>
        <button type="button" className="btn btn-white" onClick={onHome} style={{ height: 54, borderRadius: 15 }}>{done.review ? '복습으로 돌아가기' : '메인으로'}</button>
        {score.total > score.correct && (
          <button type="button" className="btn" onClick={onReview} style={{ height: 54, background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', borderRadius: 15 }}>틀린 단어 다시 보기</button>
        )}
      </div>
    </div>
  </Layout>
  );
};

export default Lesson;
